import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";

const Stat = ({ label, value, hot }) => (
  <div className={`rounded-2xl p-5 ${hot ? "bg-[#C8FF4D] text-[#0B0D10]" : "bg-white/[0.05] border border-white/[0.08]"}`} data-testid={`analytics-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="font-display font-extrabold text-[36px] leading-none tracking-[-0.03em] tabular-nums">{value}</div>
    <div className={`font-mono text-[10px] uppercase tracking-[0.16em] mt-3 ${hot ? "text-[#0B0D10]/65" : "text-white/45"}`}>{label}</div>
  </div>
);

export default function Analytics() {
  const { activeOrg } = useAuth();
  const [a, setA] = useState(null);
  const [acts, setActs] = useState([]);
  useEffect(() => {
    if (!activeOrg) return;
    api.get(`/orgs/${activeOrg.id}/analytics`).then((r) => setA(r.data));
    api.get(`/orgs/${activeOrg.id}/activity`).then((r) => setActs(r.data)).catch(() => {});
  }, [activeOrg]);
  if (!activeOrg || !a) return null;
  const secondary = [["Passes issued", a.issued], ["Wallet installs", a.installed], ["Install rate", `${a.install_rate}%`], ["Return visits", a.visits], ["Rewards earned", a.rewards_earned], ["Redemption rate", `${a.redemption_rate}%`]];
  return (
    <div className="space-y-5">
      <PageHeader title="Analytics" subtitle="Members, repeat visits and rewards redeemed. Real numbers from real passes." />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="rounded-[26px] bg-[#0B0D10] text-white p-6 sm:p-8 grain relative overflow-hidden" data-testid="analytics-panel">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#C8FF4D]/12 blur-3xl pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-5">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/45">Insights</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border border-white/10 text-white/50">Last 30 days</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Members joined" value={a.issued} />
            <Stat label="Returning members" value={`${a.repeat_rate}%`} hot />
            <Stat label="Rewards redeemed" value={a.rewards_redeemed} />
          </div>
          <div className="mt-7">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-white/45 mb-2">Visits and check-ins</div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={a.series} margin={{ left: 0, right: 0, top: 6, bottom: 0 }}>
                <defs><linearGradient id="an-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C8FF4D" stopOpacity={0.4} /><stop offset="100%" stopColor="#C8FF4D" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(d) => d.slice(5)} interval={6} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)", background: "#14181c", color: "#fff", fontSize: 12 }} />
                <Area type="monotone" dataKey="visits" stroke="#C8FF4D" strokeWidth={2.4} fill="url(#an-area)" name="Visits" />
                <Area type="monotone" dataKey="redemptions" stroke="#ffffff" strokeOpacity={0.5} strokeWidth={1.5} fill="transparent" name="Redeemed" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4 border-t border-white/10 pt-5">
            {secondary.map(([l, v]) => <div key={l} data-testid={`analytics-${l.replace(/\s/g, "-").toLowerCase()}`}><div className="font-display font-extrabold text-[22px] leading-none tabular-nums">{v}</div><div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-white/45 mt-1.5">{l}</div></div>)}
          </div>
          {acts.length > 0 && (
            <div className="mt-6 border-t border-white/10 pt-4 space-y-2.5" data-testid="analytics-activity">
              {acts.slice(0, 6).map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 text-[12.5px]"><span className="h-1.5 w-1.5 rounded-full bg-[#C8FF4D] shrink-0" /><span className="text-white/85 font-medium truncate">{ev.title}</span><span className="flex-1 text-white/45 truncate hidden sm:inline">{ev.detail}</span><span className="text-white/35 tabular-nums shrink-0">{new Date(ev.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span></div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
